import { Widget } from './widget'
export type Data = string
export namespace Data {
  export enum Type {
    Csv = 'Csv',
  }
  export namespace Type {
    export function getType(data: Data): Type {
      // TODO: Implement type detection logic
      return Type.Csv
    }
  }

  export type AiSummary<TDataType extends Data.Type = Data.Type> = {
    [Data.Type.Csv]: AiSummary.Csv
  }[TDataType]
  export namespace AiSummary {
    export interface Csv {
      sheets: Record<string, { summary: Csv.SheetSummary }>
      crossSheetInsights: string[]
    }
    export namespace Csv {
      export interface Column {
        name: string
        type: 'text' | 'number' | 'date' | 'boolean' | 'currency'
        samples: string[]
        hasNulls: boolean
        isUnique: boolean
      }

      export interface SuggestedVisualization {
        type:
          | 'metric'
          | 'charts'
          | 'details'
          | 'report-card'
          | 'comparison-table'
        chartSubtype?:
          | 'line'
          | 'bar'
          | 'bar.stacked'
          | 'donut'
          | 'scatter'
          | 'venn'
        reason: string
        columns: string[]
      }

      export interface DataCharacteristics {
        hasTimeSeries: boolean
        hasCategorical: boolean
        hasNumericalMetrics: boolean
        relationshipCount: number
      }

      export interface SheetSummary {
        recordCount: number
        columns: Column[]
        insights: string[]
        suggestedVisualizations: SuggestedVisualization[]
        dataCharacteristics: DataCharacteristics
      }

      /**
       * Generates a unified AI prompt that describes the CSV data structure and usage instructions.
       *
       * This prompt is designed to be reusable across different AI assistants (page generator,
       * widget generator, etc.) by providing a generic description of available data without
       * prescribing specific use cases.
       *
       * The generated prompt includes:
       * - Complete data structure as JSON
       * - Available sheets with their columns and insights
       * - Recommended visualizations with reasoning
       * - Cross-sheet insights for multi-sheet analysis
       * - Generic instructions for working with the data
       *
       */
      export function getAiPrompt(data: Csv): string {
        return `# Data Summary

You are provided with a CSV data summary in the following format. Use this information to understand the available data and create appropriate visualizations.

## Data Structure

${JSON.stringify(data, null, 2)}

## How to Use This Data

### Available Sheets
${Object.keys(data.sheets)
  .map(sheetName => {
    const sheet = data.sheets[sheetName]
    return `
**${sheetName}**
- Records: ${sheet.summary.recordCount}
- Columns: ${sheet.summary.columns.map(c => `${c.name} (${c.type})`).join(', ')}
- Key Insights: ${sheet.summary.insights.join('; ')}
`
  })
  .join('\n')}

### Recommended Visualizations
${Object.entries(data.sheets)
  .flatMap(([sheetName, sheet]) =>
    sheet.summary.suggestedVisualizations.map(
      viz => `- **${viz.type}${viz.chartSubtype ? ` (${viz.chartSubtype})` : ''}** for ${sheetName}: ${viz.reason}
  Columns: ${viz.columns.join(', ')}`,
    ),
  )
  .join('\n')}

### Cross-Sheet Insights
${data.crossSheetInsights.map(insight => `- ${insight}`).join('\n')}

## Instructions

When creating visualizations or pages:
1. Reference columns by their exact names as shown in the data structure
2. Consider the data characteristics (time series, categorical, numerical metrics)
3. Use the suggested visualizations as guidance, but adapt based on the specific request. Available widget types:${Widget.Type.SupportsAiGeneration.ALL.map(type => `\n   - **${type}**: ${Widget.Type.getAiDescription(type)}`).join('')}
4. Leverage cross-sheet insights when creating multi-sheet analyses
5. Pay attention to data types (text, number, date, boolean, currency) when designing queries
6. Be aware of null values and uniqueness constraints when applicable
`
      }
    }
  }
}
